/**
 * Utility functions for filtering Chakra UI props when rendering as native DOM elements
 */

// List of Chakra UI specific props that should not be passed to native DOM elements
export const CHAKRA_PROPS_TO_FILTER = [
  // Styling props
  'borderWidth',
  'borderStyle',
  'borderColor',
  'borderRadius',
  'backgroundColor',
  'bgColor',
  'color',
  'opacity',
  'width',
  'height',
  'minWidth',
  'maxWidth',
  'minHeight',
  'maxHeight',
  'padding',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'paddingX',
  'paddingY',
  'pt',
  'pr',
  'pb',
  'pl',
  'px',
  'py',
  'margin',
  'marginTop',
  'marginRight',
  'marginBottom',
  'marginLeft',
  'marginX',
  'marginY',
  'mt',
  'mr',
  'mb',
  'ml',
  'mx',
  'my',
  'position',
  'top',
  'right',
  'bottom',
  'left',
  'zIndex',
  'flexDirection',
  'justifyContent',
  'alignItems',
  'alignContent',
  'flexWrap',
  'flexGrow',
  'flexShrink',
  'flexBasis',
  'gridTemplateColumns',
  'gridTemplateRows',
  'gridTemplateAreas',
  'gridColumn',
  'gridRow',
  'gridArea',
  'columnGap',
  'rowGap',
  'gap',
  'overflow',
  'overflowX',
  'overflowY',
  'textDecoration',
  'textAlign',
  'lineHeight',
  'letterSpacing',
  'whiteSpace',
  'wordBreak',
  'textOverflow',
  'fontSize',
  'fontWeight',
  'fontFamily',
  'fontStyle',
  'boxShadow',
  'transition',
  'transform',
  'filter',
  'backdropFilter',
  'cursor',
  'pointerEvents',
  'userSelect',
  'visibility',
  'boxSizing',
  'objectFit',
  'objectPosition',
  'float',
  'clear',

  // Layout props
  'orientation',
  'spacing',
  'isFitted',
  'align',
  'justify',
  'wrap',
  'shouldWrapChildren',
  'direction',
  'divider',

  // Chakra UI pseudo props
  '_focus',
  '_disabled',
  '_hover',
  '_active',
  '_visited',
  '_focusWithin',
  '_focusVisible',
  '_invalid',
  '_readOnly',
  '_required',
  '_loading',
  '_selected',
  '_expanded',
  '_highlighted',
  '_groupHover',
  '_groupActive',
  '_groupDisabled',
  '_groupFocus',
  '_groupChecked',
  '_groupInvalid',
  '_groupReadOnly',
  '_groupRequired',
  '_groupLoading',
  '_groupSelected',
  '_groupExpanded',
  '_groupHighlighted',
  '_hoverAndFocus',
  '_activeAndFocus',
  '_focusWithinVisible',
  '_group',
  '_peer',
  '_ancestor',
  '_parent',
  '_child',
  '_descendant',

  // Chakra UI specific props
  'colorPalette',
  'variant',
  'size',
  'as',
  'sx',
  'css',
  '__css',
  '__sx',
  'layerStyle',
  'textStyle',
  'apply',

  // Animation props
  'animation',
  'transitionProperty',
  'transitionDuration',
  'transitionTimingFunction',
  'transitionDelay',

  // Other Chakra props
  'isTruncated',
  'noOfLines',
  'isFocusable',
  // NOTE: 'disabled', 'required', 'readOnly' are standard HTML attributes
  // They should NOT be filtered as they are valid for native elements
  'invalid', // This is Chakra-specific (HTML uses 'aria-invalid')
];

// List of native HTML elements that shouldn't receive styling props
export const NATIVE_HTML_ELEMENTS = [
  'a',
  'abbr',
  'address',
  'area',
  'article',
  'aside',
  'audio',
  'b',
  'base',
  'bdi',
  'bdo',
  'blockquote',
  'body',
  'br',
  'button',
  'canvas',
  'caption',
  'cite',
  'code',
  'col',
  'colgroup',
  'data',
  'datalist',
  'dd',
  'del',
  'details',
  'dfn',
  'dialog',
  'div',
  'dl',
  'dt',
  'em',
  'embed',
  'fieldset',
  'figcaption',
  'figure',
  'footer',
  'form',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'head',
  'header',
  'hr',
  'html',
  'i',
  'iframe',
  'img',
  'input',
  'ins',
  'kbd',
  'label',
  'legend',
  'li',
  'link',
  'main',
  'map',
  'mark',
  'meta',
  'meter',
  'nav',
  'noscript',
  'object',
  'ol',
  'optgroup',
  'option',
  'output',
  'p',
  'param',
  'picture',
  'pre',
  'progress',
  'q',
  'rp',
  'rt',
  'ruby',
  's',
  'samp',
  'script',
  'section',
  'select',
  'small',
  'source',
  'span',
  'strong',
  'style',
  'sub',
  'summary',
  'sup',
  'table',
  'tbody',
  'td',
  'template',
  'textarea',
  'tfoot',
  'th',
  'thead',
  'time',
  'title',
  'tr',
  'track',
  'u',
  'ul',
  'var',
  'video',
  'wbr',
];

/**
 * Filter out Chakra UI specific props from a props object
 */
export function filterChakraProps<T extends Record<string, unknown>>(props: T): Partial<T> {
  const filteredProps = {} as Partial<T>;

  Object.keys(props).forEach((key) => {
    if (!CHAKRA_PROPS_TO_FILTER.includes(key)) {
      filteredProps[key as keyof T] = props[key as keyof T];
    }
  });

  return filteredProps;
}

/**
 * Check if a component name is a native HTML element
 */
export function isNativeHTMLElement(element: string): boolean {
  if (!element || typeof element !== 'string') {
    return false;
  }
  return NATIVE_HTML_ELEMENTS.includes(element.toLowerCase());
}

/**
 * Smart prop filtering that automatically detects if the target is a native element
 */
export function filterPropsForElement<T extends Record<string, unknown>>(
  props: T,
  element?: string
): Partial<T> {
  // Handle null/undefined element safely
  if (!element) {
    return filterChakraProps(props);
  }

  // Normalize element name for consistent checking
  const normalizedElement = element.toLowerCase().trim();

  // Check if it's a native HTML element
  if (isNativeHTMLElement(normalizedElement)) {
    return filterChakraProps(props);
  }

  // Additional safety check for common native elements that might be missed
  const additionalNativeElements = ['button', 'input', 'select', 'textarea', 'a', 'div', 'span'];
  if (additionalNativeElements.includes(normalizedElement)) {
    return filterChakraProps(props);
  }

  // For custom components, pass through all props
  return props;
}

/**
 * Extended prop filtering that preserves the 'as' prop for ChakraBox
 */
export function filterPropsForElementWithAs<T extends Record<string, unknown>>(
  props: T,
  element?: string,
  _asProp?: unknown
): Partial<T> {
  // Get the filtered props
  const filteredProps = filterPropsForElement(props, element);

  // RULE 15: Do NOT add 'as' back to filteredProps
  // The Box component explicitly passes as={element}, and adding it here would override that
  // The spread {...filteredProps} comes after as={element}, so 'as' in filteredProps would win
  // This was causing <div as="label"> instead of <label>

  return filteredProps;
}
